import { format, getYear, isPast as isPastDateFns, parseISO } from "date-fns";
import { usePostHog } from "posthog-js/react";
import type React from "react";
import { Link, useNavigate } from "react-router";
import { twJoin } from "tailwind-merge";

import type { EventDto, ImageDto } from "@/api/types";
import type { ContextMenuItem } from "@/components/ContextMenu/ContextMenu";
import { ContextMenu } from "@/components/ContextMenu/ContextMenu";
import { DeleteModal, useDeleteModal } from "@/components/DeleteModal/DeleteModal";
import { EventBadges } from "@/components/EventBadges/EventBadges";
import { EventSignUpWithGuestsModal } from "@/components/EventSignUpWithGuestsModal/EventSignUpWithGuestsModal";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { Copy01Icon } from "@/components/Icons/Copy01Icon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { LinkExternal01Icon } from "@/components/Icons/LinkExternal01Icon";
import { PlusIcon } from "@/components/Icons/PlusIcon";
import { Trash02Icon } from "@/components/Icons/Trash02Icon";
import { useShareEntityMenuItems } from "@/components/ShareButton/helpers";
import { ShareToChatModal } from "@/components/ShareToChatModal/ShareToChatModal";
import { ShareToCommunityFeedModal } from "@/components/ShareToCommunityFeedModal/ShareToCommunityFeedModal";
import { UserAvatarList } from "@/components/UserAvatarList/UserAvatarList";
import { cardSurfaceVariants } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { Spread } from "@/design-system/Spread";
import { ToggleButton } from "@/design-system/ToggleButton";
import { canParticipateEvent, computeMaxGuests, getIsEventSharable } from "@/helpers/events";
import { canManageEvents } from "@/helpers/permissions";
import { twResolve } from "@/helpers/tw-resolve";
import { isDefined } from "@/helpers/util";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { eventMutations } from "@/queries/events/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

interface EventCardProps {
  event: EventDto;
  isPast?: boolean;
}

export function EventCard({ event, isPast }: EventCardProps): React.ReactNode {
  const [isShareToCommunityFeedModalOpened, shareToCommunityFeedModalHandler] = useBool(false);
  const [isSignUpWithGuestsModalOpen, signUpWithGuestsModalHandler] = useBool(false);
  const [isShareToChatModalOpened, shareToChatModalHandler] = useBool(false);

  const slug = useSlug();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const postHog = usePostHog();
  const hasPermission = usePermission();
  const showFlashToast = useFlashToast();
  const { componentProps: deleteModalProps, openDeleteModal } = useDeleteModal<string>("delete-event-modal");
  const shareEntityMenuItems = useShareEntityMenuItems({
    deepLinkPayload: {
      entityType: "event",
      entityId: event.id,
    },
    callbacks: {
      shareToCommunityFeed: shareToCommunityFeedModalHandler.setTrue,
      shareToChat: shareToChatModalHandler.setTrue,
    },
  });

  const deleteEventMutation = eventMutations.useDeleteEvent();
  const signUpMutation = eventMutations.useEventSignUp();
  const signOffMutation = eventMutations.useEventSignOff();

  const isLoading = signUpMutation.isPending || signOffMutation.isPending;

  function getSpotsBadge(): string | null {
    const participantsExist = isDefined(event.maxParticipants) && isDefined(event.participantCount);
    const isFull = participantsExist ? event.participantCount! >= event.maxParticipants! : false;
    const restSpotsAmount = participantsExist && !isFull ? event.maxParticipants! - event.participantCount! : null;
    const noLimit = event.maxParticipants == null;
    if (event.isSignedUp) {
      return t("page.calendar.events.spots.is-signed-up");
    }
    if (isFull) {
      return t("page.calendar.events.spots.no-spots");
    }
    if (!event.canParticipate) {
      return null;
    }
    if (isDefined(restSpotsAmount)) {
      return t("page.calendar.events.spots.free-spots", {
        count: event.maxParticipants! - event.participantCount!,
      });
    }
    if (noLimit) {
      return t("page.calendar.events.spots.is-not-signed-up");
    }

    return null;
  }
  const maxGuests = computeMaxGuests(event);

  const handleToggleParticipation = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (event.isSignedUp) {
      signOffMutation.mutate(event.id);
    } else if (maxGuests <= 1) {
      signUpMutation.mutate({ eventId: event.id, guestCount: 0 });
    } else {
      signUpWithGuestsModalHandler.setTrue();
    }
  };

  const handleSignUpWithGuests = (guestCount: number) => {
    signUpMutation.mutate(
      { eventId: event.id, guestCount },
      { onSuccess: () => signUpWithGuestsModalHandler.setFalse() },
    );
  };

  const handleDuplicateEvent = () => {
    void navigate(
      routes.events.create({
        slug,
      }),
      {
        state: {
          id: event.id,
        },
      },
    );
  };

  const handleEditEvent = () => {
    if (isPastDateFns(event.startsAt)) {
      showFlashToast({
        variant: "neutral",
        message: t("page.calendar.events.edit.notification.is-started"),
      });

      return;
    }
    void navigate(
      routes.events.edit({
        slug,
        id: event.id,
      }),
    );
  };

  const handleClickJoinExternalEvent = () => {
    if (!event.externalPartyUrl) return;

    window.open(event.externalPartyUrl, "_blank");
  };

  const menuActions: ContextMenuItem[] = [
    {
      labels: {
        default: t("common.action.edit"),
      },
      "data-testid": "context-menu-edit-btn",
      value: "edit",
      icon: <Edit05Icon />,
      isHidden: !event.canEdit,
      callbackFn: handleEditEvent,
    },
    {
      labels: {
        default: t("common.action.duplicate"),
      },
      value: "duplicate",
      icon: <Copy01Icon />,
      type: "button",
      callbackFn: handleDuplicateEvent,
      isHidden: !hasPermission(canManageEvents),
    },
    {
      labels: {
        default: t("common.action.delete"),
      },
      value: "delete",
      icon: <Trash02Icon />,
      type: "button",
      isHidden: !event.canDelete,
      callbackFn: () => openDeleteModal(event.id),
    },
    ...(getIsEventSharable(event) ? shareEntityMenuItems : []),
  ];

  const day = parseISO(event.startsAt).getDate();
  const month = formatDate(i18n, "monthShort", parseISO(event.startsAt));
  const year = getYear(event.startsAt);
  const amountParticipants = event.participants.length;
  const isJoinBtnVisible = canParticipateEvent(event);

  return (
    <>
      <Link
        to={routes.events.details({
          slug,
          id: event.id,
        })}
        data-testid="event-card"
        className={twJoin(cardSurfaceVariants({ interactive: true }), twResolve("relative", isPast && "opacity-70"))}
        onClick={() => postHog.capture("clicked_event")}
      >
        <article className="flex size-full gap-space-md p-card-compact">
          {/* Left */}
          <time className="flex shrink-0 flex-col items-center justify-center rounded-md bg-background-on-default px-space-sm py-space-md text-text-secondary">
            <span className="text-heading-2 leading-none">{day}</span>
            <span className="text-caption">{month}</span>
            <span className="text-caption">{year}</span>
          </time>
          {/* Right */}
          <section className="flex min-w-0 flex-1 flex-col gap-space-sm overflow-hidden">
            <Inline gap="xs">
              <EventBadges event={event} />
            </Inline>
            <Spread space="sm" align="start">
              <section className="flex w-full flex-1 flex-col gap-space-xs">
                <span className="w-full truncate text-body-emphasis">{event.name}</span>
                <span className="flex flex-wrap items-center gap-space-xs text-text-secondary">
                  <span className="text-caption whitespace-nowrap">{`${format(parseISO(event.startsAt), "HH:mm")} - ${format(parseISO(event.endsAt), "HH:mm")}`}</span>
                  {event.location && <span className="text-caption">• {event.location}</span>}
                  {event.updatedAt && (
                    <span className="text-caption-emphasis whitespace-nowrap">
                      • {t("page.calendar.events.edited")}
                    </span>
                  )}
                </span>
                {!isPast && <span className="text-caption text-text-secondary">{getSpotsBadge()}</span>}
              </section>
              <ContextMenu items={menuActions} />
            </Spread>
            {(isJoinBtnVisible || amountParticipants > 0) && (
              <footer className="mt-auto flex w-full items-end gap-space-sm">
                {amountParticipants > 0 && (
                  <UserAvatarList
                    images={event.participants.filter((x) => x.avatar !== undefined).map((x) => x.avatar as ImageDto)}
                    limit={3}
                    totalElements={event.participantCount}
                  />
                )}
                {isJoinBtnVisible && (
                  <span className="ml-auto">
                    {event.type === "external" && (
                      // Same look & feel button but no toggling state
                      <ToggleButton
                        isChecked={false}
                        size="sm"
                        config={{
                          checked: {
                            label: t("model.event.details.btn.sign-off"),
                            icon: <CheckIcon />,
                          },
                          unchecked: {
                            label: t("model.event.details.btn.sign-up"),
                            icon: <LinkExternal01Icon />,
                          },
                        }}
                        disabled={!!event.deletedAt}
                        data-testid="event-external-sign-up-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          handleClickJoinExternalEvent();
                        }}
                      />
                    )}
                    {event.type !== "external" && (
                      <ToggleButton
                        config={{
                          checked: {
                            label: t("model.event.details.btn.sign-off"),
                            icon: <CheckIcon />,
                          },
                          unchecked: {
                            label: t("model.event.details.btn.sign-up"),
                            icon: <PlusIcon />,
                          },
                        }}
                        disabled={!!event.deletedAt}
                        size="sm"
                        onClick={handleToggleParticipation}
                        isChecked={event.isSignedUp}
                        {...{
                          isLoading,
                        }}
                      />
                    )}
                  </span>
                )}
              </footer>
            )}
          </section>
        </article>
      </Link>

      <EventSignUpWithGuestsModal
        eventName={event.name}
        isOpened={isSignUpWithGuestsModalOpen}
        onOpenChange={signUpWithGuestsModalHandler.set}
        maxGuests={maxGuests}
        isLoading={signUpMutation.isPending}
        onConfirm={handleSignUpWithGuests}
      />
      <ShareToCommunityFeedModal
        isOpened={isShareToCommunityFeedModalOpened}
        onOpenChange={shareToCommunityFeedModalHandler.set}
        entityId={event.id}
        entityType="event"
      />
      <ShareToChatModal
        isOpened={isShareToChatModalOpened}
        onOpenChange={shareToChatModalHandler.set}
        entityId={event.id}
        entityType="event"
      />
      <DeleteModal
        title={t("page.calendar.events.delete.modal.title")}
        description={t("page.calendar.events.delete.modal.description")}
        onDelete={(eventId) => deleteEventMutation.mutateAsync(eventId)}
        deleteBtnProps={{
          "data-testid": "delete-event-modal",
        }}
        {...deleteModalProps}
      />
    </>
  );
}
